import { CreatorInfo } from "@/components/CreatorInfo";
import { InvoicePayments } from "@/components/PaymentLinks";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreateLinkedDocumentDialog } from "@/components/CreateLinkedDocumentDialog";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader, PageBody, Label as PageLabel } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Download, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtMoney, toMinor } from "@/lib/format";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { RowDeleteButton } from "@/components/RowDeleteButton";
import { SentEmailChip, SentEmailBlock } from "@/components/SentEmail";
import { FlagButton, FlagBadge } from "@/components/FlagButton";

const STATUS = {
  draft:    "bg-slate-100 text-slate-700 border-slate-200",
  pending:  "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  sent:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  void:     "bg-rose-50 text-rose-700 border-rose-200",
};

const EMPTY_BUYER = { name: "", company: "", address: "", email: "", phone: "" };
const EMPTY_LINE  = { item_code: "", description: "", quantity: 1, tax_rate: "", unit_price: "" };
const newKey = () => (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
const newLine = () => ({ ...EMPTY_LINE, _key: newKey() });

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [prefix, setPrefix] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [emailed, setEmailed] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openLinkedCreate, setOpenLinkedCreate] = useState(false);
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(searchParams.get("id"));
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (prefix) params.prefix = prefix;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (emailed) params.emailed = emailed;
    const { data } = await api.get("/invoices", { params });
    setRows(data);
    setLoading(false);
  }, [status, prefix, dateFrom, dateTo, emailed]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    (async () => {
      const { data } = await api.get(`/invoices/${selectedId}`);
      setSelected(data);
    })();
  }, [selectedId]);

  return (
    <>
      <PageHeader title="Invoices" subtitle="Full lifecycle from Draft → Pending → Approved → Sent. Numbers assigned on approval.">
        <Button variant="outline" onClick={() => setOpenCreate(true)} data-testid="new-invoice-button">
          Manual / free-text
        </Button>
        <Button onClick={() => setOpenLinkedCreate(true)} data-testid="new-linked-invoice-button">
          <Plus className="w-4 h-4 mr-1.5" /> New invoice
        </Button>
      </PageHeader>
      <PageBody>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4 mb-4 flex flex-wrap items-end gap-3">
          <Filter label="Status">
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </Filter>
          <Filter label="Prefix">
            <Select value={prefix || "all"} onValueChange={(v) => setPrefix(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="filter-prefix"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="AIEM-INV">AIEM-INV (standard)</SelectItem>
                <SelectItem value="STRIOR">STRIOR (Stripe)</SelectItem>
              </SelectContent>
            </Select>
          </Filter>
          <Filter label="Date from">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 font-mono text-sm" data-testid="filter-date-from" />
          </Filter>
          <Filter label="Date to">
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 font-mono text-sm" data-testid="filter-date-to" />
          </Filter>
          <Filter label="Emailed">
            <Select value={emailed || "all"} onValueChange={(v) => setEmailed(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="filter-emailed"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Emailed</SelectItem>
                <SelectItem value="no">Not emailed</SelectItem>
              </SelectContent>
            </Select>
          </Filter>
          {(status || prefix || dateFrom || dateTo || emailed) && (
            <Button variant="ghost" size="sm" onClick={() => { setStatus(""); setPrefix(""); setDateFrom(""); setDateTo(""); setEmailed(""); }}>Clear</Button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">Invoice #</TableHead>
                <TableHead>Student / Buyer</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Last emailed</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-500">
                  No invoices match. Create one manually or record a receipt to auto-generate a Draft.
                </TableCell></TableRow>
              )}
              {rows.map((inv) => (
                <TableRow key={inv.id} className="cursor-pointer" onClick={() => setSelectedId(inv.id)} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono text-xs">{inv.number || <span className="text-slate-400 italic">— unassigned —</span>}</TableCell>
                  <TableCell>
                    <div className="text-sm">{inv.buyer?.name || "—"}</div>
                    {inv.buyer?.email && <div className="text-xs text-slate-500">{inv.buyer.email}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtMoney(inv.total_minor, inv.currency)}<div className="text-[10px] text-slate-500 mt-1" data-testid={`invoice-row-outstanding-${inv.id}`}>Open {fmtMoney(inv.outstanding_minor, inv.currency)} · {inv.payment_status}</div></TableCell>
                  <TableCell className="text-xs font-mono">{inv.currency}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS[inv.status]}>{inv.status.toUpperCase()}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">{inv.prefix}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtDate(inv.date_issued)}</TableCell>
                  <TableCell className="text-xs"><span data-testid={`invoice-source-${inv.id}`}>{inv.gateway || inv.source_type}</span>{inv.gateway_mode === "test" && <span className="block text-amber-700 text-[10px]" data-testid={`invoice-source-test-${inv.id}`}>TEST</span>}</TableCell>
                  <TableCell><SentEmailChip record={inv} testId={`invoice-last-emailed-${inv.id}`} /></TableCell>
                  <TableCell className="w-10 py-1" onClick={(e) => e.stopPropagation()}>
                    <RowDeleteButton
                      endpoint={`/invoices/${inv.id}`}
                      label={`invoice ${inv.number || inv.id.slice(0, 8)}`}
                      onDeleted={load}
                      testId={`invoice-delete-${inv.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </PageBody>

      <CreateLinkedDocumentDialog kind="invoice" open={openLinkedCreate} onOpenChange={setOpenLinkedCreate}
        onCreated={(invoice) => { setOpenLinkedCreate(false); load(); setSelectedId(invoice.id); }} />
      <CreateInvoiceDialog open={openCreate} onOpenChange={setOpenCreate}
                            onCreated={(id) => { setOpenCreate(false); load(); setSelectedId(id); }} />

      <Sheet open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="min-w-[640px] sm:min-w-[720px] overflow-y-auto" data-testid="invoice-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-mono" data-testid="invoice-detail-title">{selected ? (selected.number || "DRAFT (no number yet)") : "Invoice details"}</SheetTitle>
            <SheetDescription data-testid="invoice-detail-description">{selected?.buyer?.name || "Review invoice details and lifecycle actions."}</SheetDescription>
          </SheetHeader>
          {selected && (
            <InvoiceDetail
              invoice={selected}
              onChanged={async (closed) => {
                if (closed) { setSelectedId(null); setSelected(null); load(); return; }
                const { data } = await api.get(`/invoices/${selectedId}`);
                setSelected(data);
                load();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function Filter({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function CreateInvoiceDialog({ open, onOpenChange, onCreated }) {
  const [prefix, setPrefix] = useState("AIEM-INV");
  const [currency, setCurrency] = useState("MYR");
  const [sstRate, setSstRate] = useState("8");
  const [terms, setTerms] = useState("30 Days");
  const [dateIssued, setDateIssued] = useState(new Date().toISOString().slice(0, 10));
  const [soNo, setSoNo] = useState("");
  const [uuidField, setUuidField] = useState("");
  const [buyer, setBuyer] = useState({ ...EMPTY_BUYER });
  const [lines, setLines] = useState([newLine()]);
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    const gross = lines.reduce((s, l) => s + Math.round(toMinor(l.unit_price) * (parseFloat(l.quantity) || 1)), 0);
    const sst = Math.round(gross * (parseFloat(sstRate) || 0) / 100);
    return { gross, sst, total: gross + sst };
  }, [lines, sstRate]);

  const reset = () => {
    setPrefix("AIEM-INV"); setCurrency("MYR"); setSstRate("8"); setTerms("30 Days");
    setDateIssued(new Date().toISOString().slice(0, 10));
    setSoNo(""); setUuidField("");
    setBuyer({ ...EMPTY_BUYER });
    setLines([newLine()]);
  };

  const submit = async () => {
    if (!buyer.name) { toast.error("Buyer name is required"); return; }
    if (lines.some((l) => !l.item_code || !l.description || !l.unit_price)) {
      toast.error("Every line needs item code, description and price"); return;
    }
    setSaving(true);
    try {
      const payload = {
        prefix, source_type: "manual", currency,
        sst_rate: parseFloat(sstRate) || 0,
        date_issued: dateIssued, terms,
        our_so_no: soNo || null, uuid_field: uuidField || null,
        buyer: {
          name: buyer.name,
          company: buyer.company || null,
          address: buyer.address || null,
          email: buyer.email || null,
          phone: buyer.phone || null,
        },
        lines: lines.map((l) => {
          const qty = parseFloat(l.quantity) || 1;
          const unit = toMinor(l.unit_price);
          return {
            item_code: l.item_code, description: l.description,
            quantity: qty, tax_rate: l.tax_rate || null,
            unit_price_minor: unit, amount_minor: Math.round(unit * qty),
          };
        }),
      };
      // Strip null email so pydantic doesn't reject with regex parsing
      if (!payload.buyer.email) delete payload.buyer.email;
      const { data } = await api.post("/invoices", payload);
      toast.success("Draft invoice created");
      reset();
      onCreated(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create manual invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Prefix">
              <Select value={prefix} onValueChange={setPrefix}>
                <SelectTrigger data-testid="create-prefix"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AIEM-INV">AIEM-INV</SelectItem>
                  <SelectItem value="STRIOR">STRIOR</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Currency">
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MYR">MYR</SelectItem>
                  <SelectItem value="SGD">SGD</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="SST %">
              <Input type="number" step="0.01" value={sstRate} onChange={(e) => setSstRate(e.target.value)} className="font-mono" data-testid="create-sst" />
            </Field>
            <Field label="Terms"><Input value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="create-terms" /></Field>
            <Field label="Date issued"><Input type="date" value={dateIssued} onChange={(e) => setDateIssued(e.target.value)} className="font-mono" /></Field>
            <Field label="Our S/O No"><Input value={soNo} onChange={(e) => setSoNo(e.target.value)} className="font-mono" /></Field>
            <Field label="UUID"><Input value={uuidField} onChange={(e) => setUuidField(e.target.value)} className="font-mono" /></Field>
          </div>

          <div>
            <PageLabel>Billed to</PageLabel>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Field label="Buyer name"><Input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid="create-buyer-name" /></Field>
              <Field label="Company"><Input value={buyer.company} onChange={(e) => setBuyer({ ...buyer, company: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} data-testid="create-buyer-email" /></Field>
              <Field label="Phone"><Input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} /></Field>
              <div className="col-span-2"><Field label="Address"><Textarea rows={2} value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} /></Field></div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <PageLabel>Line items</PageLabel>
              <Button size="sm" variant="outline" onClick={() => setLines([...lines, newLine()])}>
                + Line
              </Button>
            </div>
            {lines.map((l, i) => (
              <div key={l._key || i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-3"><Input placeholder="Item code" value={l.item_code} onChange={(e) => update(lines, setLines, i, "item_code", e.target.value)} className="text-xs font-mono" data-testid={`create-line-code-${i}`} /></div>
                <div className="col-span-4"><Input placeholder="Description" value={l.description} onChange={(e) => update(lines, setLines, i, "description", e.target.value)} data-testid={`create-line-desc-${i}`} /></div>
                <div className="col-span-1"><Input type="number" min="0" step="0.01" placeholder="Qty" value={l.quantity} onChange={(e) => update(lines, setLines, i, "quantity", e.target.value)} className="text-right font-mono" /></div>
                <div className="col-span-1"><Input placeholder="Tax rate" value={l.tax_rate} onChange={(e) => update(lines, setLines, i, "tax_rate", e.target.value)} className="text-right font-mono" /></div>
                <div className="col-span-2"><Input type="number" step="0.01" placeholder="Price/Unit" value={l.unit_price} onChange={(e) => update(lines, setLines, i, "unit_price", e.target.value)} className="text-right font-mono" data-testid={`create-line-price-${i}`} /></div>
                <div className="col-span-1">
                  <Button variant="ghost" size="icon" onClick={() => { if (lines.length > 1) setLines(lines.filter((_, x) => x !== i)); }}>
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="text-right text-sm mt-2 space-y-0.5">
              <div className="text-slate-500">Gross <span className="font-mono ml-4">{fmtMoney(totals.gross, currency)}</span></div>
              <div className="text-slate-500">Services Tax <span className="font-mono ml-4">{fmtMoney(totals.sst, currency)}</span></div>
              <div className="font-semibold text-slate-900">Total <span className="font-mono ml-4 tabular-nums" data-testid="create-total">{fmtMoney(totals.total, currency)}</span></div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="create-invoice-submit">{saving ? "Saving…" : "Create as Draft"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function update(list, setter, idx, field, value) {
  const copy = [...list]; copy[idx] = { ...copy[idx], [field]: value }; setter(copy);
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function InvoiceDetail({ invoice, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [openSend, setOpenSend] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const act = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/${path}`, body || null);
      toast.success(`Invoice ${path}`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="mt-6 space-y-5 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={STATUS[invoice.status]}>{invoice.status.toUpperCase()}</Badge>
          <span className="text-slate-400">·</span>
          <span className="font-mono text-xs">{invoice.prefix}</span>
          <span className="text-slate-400">·</span>
          <span className="text-xs text-slate-500">Source: {invoice.source_type}</span>
        </div>

        <div className="grid grid-cols-3 gap-3 text-xs">
          <Info label="Date">{fmtDate(invoice.date_issued)}</Info>
          <Info label="Terms">{invoice.terms}</Info>
          <Info label="Currency">{invoice.currency}</Info>
          {invoice.student_id && <Info label="Student / Learner"><Link to={`/students?id=${invoice.student_id}`} className="text-blue-700 underline" data-testid="invoice-student-link">View student profile</Link></Info>}
          {invoice.from_proforma_id && <Info label="Source document"><Link to={`/proformas?id=${invoice.from_proforma_id}`} className="text-blue-700 underline" data-testid="invoice-source-proforma">Quotation / Pro-forma · R{invoice.from_proforma_revision || 1}</Link></Info>}
          <Info label="Our S/O No">{invoice.sale_id ? <Link to={`/sales?id=${invoice.sale_id}`} className="text-blue-700 underline" data-testid="invoice-linked-sale">{invoice.our_so_no || "View linked Sale"}</Link> : (invoice.our_so_no || "—")}</Info>
          <Info label="UUID">{invoice.uuid_field || "—"}</Info>
          <Info label="Page">{invoice.page || "1 of 1"}</Info>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Billed to</div>
          <div className="border border-slate-200 rounded p-3 text-xs space-y-0.5">
            <div className="font-medium">{invoice.buyer?.company || invoice.buyer?.name}</div>
            {invoice.buyer?.company && <div>{invoice.buyer?.name}</div>}
            {invoice.buyer?.address && <div className="text-slate-600 whitespace-pre-wrap">{invoice.buyer.address}</div>}
            {invoice.buyer?.email && <div className="font-mono">Email: {invoice.buyer.email}</div>}
            {invoice.buyer?.phone && <div className="font-mono">TEL: {invoice.buyer.phone}</div>}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Lines</div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right w-14">Qty</TableHead>
                  <TableHead className="text-right w-20">Tax</TableHead>
                  <TableHead className="text-right w-28">Price/Unit</TableHead>
                  <TableHead className="text-right w-28">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(invoice.lines || []).map((l, i) => (
                  <TableRow key={`${l.item_code || "line"}-${i}`}>
                    <TableCell className="text-xs font-mono">{l.item_code}</TableCell>
                    <TableCell className="text-xs">{l.description}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{(l.quantity ?? 1).toString()}</TableCell>
                    <TableCell className="text-right text-xs">{l.tax_rate || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMoney(l.unit_price_minor, invoice.currency)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMoney(l.amount_minor, invoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div />
          <div className="border border-slate-200 rounded p-3 text-sm space-y-1 font-mono tabular-nums">
            <div className="flex justify-between"><span>Gross ({invoice.currency})</span><span>{fmtMoney(invoice.gross_minor, invoice.currency)}</span></div>
            <div className="flex justify-between" data-testid="invoice-sst-summary"><span>SST ({invoice.sst_rate}%)</span><span>{fmtMoney(invoice.services_tax_minor, invoice.currency)}</span></div>
            <div className="text-xs text-slate-500" data-testid="invoice-pricing-treatment">{invoice.pricing_mode === "inclusive" ? "Entered prices include SST" : "SST added to entered prices"}</div>
            <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1"><span>Total</span><span data-testid="detail-total">{fmtMoney(invoice.total_minor, invoice.currency)}</span></div>
          </div>
        </div>

        <CreatorInfo record={invoice} kind="invoice" />
        {invoice.gateway_transaction_id && <div className="text-xs border rounded p-3 space-y-1" data-testid="invoice-gateway-source"><Link className="text-blue-700 underline" to={`/transactions?id=${invoice.gateway_transaction_id}`} data-testid="invoice-gateway-transaction-link">Gateway transaction · {invoice.gateway}</Link>{invoice.gateway_mode === "test" && <p className="text-amber-800 font-semibold" data-testid="invoice-gateway-test-warning">TEST payment — do not treat this as live revenue</p>}<p>Prepared from a payment notification. Review product, billing and SST details before approval.</p></div>}
        <InvoicePayments invoice={invoice} onChanged={onChanged} />
        <div className="text-xs text-slate-600 italic">{invoice.amount_in_words}</div>

        <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
          {(invoice.status === "draft" || invoice.status === "pending") && (
            <Button size="sm" variant="outline" onClick={() => setOpenEdit(true)} data-testid="edit-invoice">Edit</Button>
          )}
          {invoice.status === "draft" && (
            <Button size="sm" onClick={() => act("submit")} disabled={busy} data-testid="submit-invoice">Submit for review</Button>
          )}
          {invoice.status === "pending" && (
            <Button size="sm" onClick={() => act("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-invoice">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
            </Button>
          )}
          {invoice.status === "approved" && (
            <Button size="sm" onClick={() => setOpenSend(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="send-invoice">
              <Send className="w-4 h-4 mr-1.5" /> Send to student
            </Button>
          )}
          {invoice.status !== "void" && (
            <Button size="sm" variant="outline" onClick={() => act("void", null)} disabled={busy}>Void</Button>
          )}
          <a href={`${API}/invoices/${invoice.id}/pdf`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" data-testid="download-invoice-pdf"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
          </a>
          <DeleteRecordButton
            resource="invoices"
            id={invoice.id}
            status={invoice.status}
            label={`invoice ${invoice.number || "DRAFT"}`}
            testId="delete-invoice-button"
            onDeleted={() => onChanged(true)}
          />
          <FlagButton resource="invoices" id={invoice.id} flag={invoice.flag} onChanged={() => onChanged()} testId="flag-invoice-button" />
        </div>

        {invoice.sent_to && (
          <SentEmailBlock record={invoice} testId="invoice-sent-email-block" />
        )}
      </div>

      <SendDialog open={openSend} onOpenChange={setOpenSend}
                   defaultTo={invoice.buyer?.email || ""}
                   onSent={async (to) => { setOpenSend(false); await act("send", { to }); }} />
      <EditDialog open={openEdit} onOpenChange={setOpenEdit}
                   invoice={invoice}
                   onSaved={async () => { setOpenEdit(false); onChanged(); }} />
    </>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-900 text-sm">{children}</div>
    </div>
  );
}

function SendDialog({ open, onOpenChange, defaultTo, onSent }) {
  const [to, setTo] = useState(defaultTo || "");
  useEffect(() => { setTo(defaultTo || ""); }, [defaultTo]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Send invoice to student</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">The finalised PDF stays available under the Download PDF button after sending.</p>
          <div>
            <Label className="text-xs uppercase">Recipient email</Label>
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="send-email-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSent(to)} disabled={!to} data-testid="send-email-submit">Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ open, onOpenChange, invoice, onSaved }) {
  const [buyer, setBuyer] = useState(invoice.buyer || { ...EMPTY_BUYER });
  const [sstRate, setSstRate] = useState(String(invoice.sst_rate ?? 8));
  const [pricingMode, setPricingMode] = useState(invoice.pricing_mode || "exclusive");
  const [terms, setTerms] = useState(invoice.terms || "30 Days");
  const [soNo, setSoNo] = useState(invoice.our_so_no || "");
  const [uuidField, setUuidField] = useState(invoice.uuid_field || "");
  const [dateIssued, setDateIssued] = useState(invoice.date_issued || "");
  const [lines, setLines] = useState(
    (invoice.lines || []).map((l) => ({
      item_code: l.item_code, description: l.description,
      quantity: l.quantity, tax_rate: l.tax_rate || "",
      unit_price: (l.unit_price_minor / 100).toFixed(2),
      _key: newKey(),
    }))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBuyer(invoice.buyer || { ...EMPTY_BUYER });
    setSstRate(String(invoice.sst_rate ?? 8));
    setPricingMode(invoice.pricing_mode || "exclusive");
    setTerms(invoice.terms || "30 Days");
    setSoNo(invoice.our_so_no || "");
    setUuidField(invoice.uuid_field || "");
    setDateIssued(invoice.date_issued || "");
    setLines((invoice.lines || []).map((l) => ({
      item_code: l.item_code, description: l.description, quantity: l.quantity,
      tax_rate: l.tax_rate || "", unit_price: (l.unit_price_minor / 100).toFixed(2),
      _key: newKey(),
    })));
  }, [invoice]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        buyer: {
          ...buyer,
          name: buyer.name, company: buyer.company || null,
          address: buyer.address || null,
          email: buyer.email || null, phone: buyer.phone || null,
        },
        pricing_mode: pricingMode,
        sst_rate: parseFloat(sstRate) || 0,
        terms, our_so_no: soNo || null, uuid_field: uuidField || null,
        date_issued: dateIssued,
        lines: lines.map((l) => {
          const qty = parseFloat(l.quantity) || 1;
          const unit = toMinor(l.unit_price);
          return {
            item_code: l.item_code, description: l.description,
            quantity: qty, tax_rate: l.tax_rate || null,
            unit_price_minor: unit, amount_minor: Math.round(unit * qty),
          };
        }),
      };
      if (!payload.buyer.email) delete payload.buyer.email;
      await api.patch(`/invoices/${invoice.id}`, payload);
      toast.success("Invoice updated");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Date issued"><Input type="date" value={dateIssued} onChange={(e) => setDateIssued(e.target.value)} className="font-mono" /></Field>
            <Field label="Terms"><Input value={terms} onChange={(e) => setTerms(e.target.value)} /></Field>
            <Field label="SST %"><Input type="number" step="0.01" value={sstRate} onChange={(e) => setSstRate(e.target.value)} className="font-mono" data-testid="edit-sst" /></Field>
            <Field label="Price treatment"><Select value={pricingMode} onValueChange={setPricingMode}><SelectTrigger data-testid="edit-pricing-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="exclusive" data-testid="edit-pricing-exclusive">Prices exclude SST</SelectItem><SelectItem value="inclusive" data-testid="edit-pricing-inclusive">Prices include SST</SelectItem></SelectContent></Select></Field>
            <Field label="Our S/O No"><Input value={soNo} onChange={(e) => setSoNo(e.target.value)} className="font-mono" /></Field>
            <Field label="UUID"><Input value={uuidField} onChange={(e) => setUuidField(e.target.value)} className="font-mono" /></Field>
          </div>
          <div>
            <PageLabel>Billed to</PageLabel>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Field label="Name"><Input value={buyer.name || ""} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} /></Field>
              <Field label="Company"><Input value={buyer.company || ""} onChange={(e) => setBuyer({ ...buyer, company: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={buyer.email || ""} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} /></Field>
              <Field label="Phone"><Input value={buyer.phone || ""} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} /></Field>
              <div className="col-span-2"><Field label="Address"><Textarea rows={2} value={buyer.address || ""} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} /></Field></div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <PageLabel>Line items</PageLabel>
              <Button size="sm" variant="outline" onClick={() => setLines([...lines, newLine()])}>+ Line</Button>
            </div>
            {lines.map((l, i) => (
              <div key={l._key || i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-3"><Input placeholder="Item code" value={l.item_code} onChange={(e) => update(lines, setLines, i, "item_code", e.target.value)} className="text-xs font-mono" /></div>
                <div className="col-span-4"><Input placeholder="Description" value={l.description} onChange={(e) => update(lines, setLines, i, "description", e.target.value)} /></div>
                <div className="col-span-1"><Input type="number" step="0.01" placeholder="Qty" value={l.quantity} onChange={(e) => update(lines, setLines, i, "quantity", e.target.value)} className="text-right font-mono" /></div>
                <div className="col-span-1"><Input placeholder="Tax" value={l.tax_rate} onChange={(e) => update(lines, setLines, i, "tax_rate", e.target.value)} className="text-right font-mono" /></div>
                <div className="col-span-2"><Input type="number" step="0.01" placeholder="Price" value={l.unit_price} onChange={(e) => update(lines, setLines, i, "unit_price", e.target.value)} className="text-right font-mono" /></div>
                <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => { if (lines.length > 1) setLines(lines.filter((_, x) => x !== i)); }}><Trash2 className="w-4 h-4 text-slate-400" /></Button></div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-invoice">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
